import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  X,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Download,
  ExternalLink,
  Image as ImageIcon
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ImagePreviewContextType {
  openPreview: (src: string, title?: string) => void;
  closePreview: () => void;
  isPreviewOpen: boolean;
}

const ImagePreviewContext = createContext<ImagePreviewContextType>({
  openPreview: () => {},
  closePreview: () => {},
  isPreviewOpen: false,
});

export const useImagePreview = () => useContext(ImagePreviewContext);

interface ImagePreviewProviderProps {
  children: React.ReactNode;
}

export const ImagePreviewProvider: React.FC<ImagePreviewProviderProps> = ({ children }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [title, setTitle] = useState<string>('');
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);

  const openPreview = useCallback((imageSrc: string, imageTitle?: string) => {
    if (!imageSrc || imageSrc.startsWith('data:image/svg+xml')) return;
    setSrc(imageSrc);
    setTitle(imageTitle || 'Image Preview');
    setZoom(1);
    setRotation(0);
  }, []);

  const closePreview = useCallback(() => {
    setSrc(null);
    setZoom(1);
    setRotation(0);
  }, []);

  // Global click delegate listener for ALL <img> tags across the entire web app
  useEffect(() => {
    const handleGlobalImageClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Find closest <img> or element with data-preview-src
      const imgElement = target.closest('img') || target.closest('[data-preview-src]');
      if (!imgElement) return;

      // Ignore explicitly disabled images (e.g. logos or icons with data-no-preview)
      if (imgElement.hasAttribute('data-no-preview')) return;

      let imageSrc: string | null = null;
      let imageTitle: string | undefined = undefined;

      if (imgElement instanceof HTMLImageElement) {
        imageSrc = imgElement.currentSrc || imgElement.src;
        imageTitle = imgElement.alt || imgElement.title;
      } else if (imgElement.hasAttribute('data-preview-src')) {
        imageSrc = imgElement.getAttribute('data-preview-src');
        imageTitle = imgElement.getAttribute('data-preview-title') || undefined;
      }

      // Check valid image URL (ignore tiny spacer images / SVGs)
      if (imageSrc && !imageSrc.endsWith('.svg') && !imageSrc.includes('data:image/svg')) {
        // Prevent default navigation if image was wrapped inside a link
        // unless explicitly marked to follow link
        if (!imgElement.hasAttribute('data-allow-link')) {
          event.preventDefault();
          event.stopPropagation();
        }
        openPreview(imageSrc, imageTitle);
      }
    };

    document.addEventListener('click', handleGlobalImageClick, true);
    return () => {
      document.removeEventListener('click', handleGlobalImageClick, true);
    };
  }, [openPreview]);

  // Keyboard shortcut listener (ESC to close, + / - to zoom, R to rotate)
  useEffect(() => {
    if (!src) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closePreview();
      } else if (e.key === '+' || e.key === '=') {
        setZoom((z) => Math.min(z + 0.25, 4));
      } else if (e.key === '-') {
        setZoom((z) => Math.max(z - 0.25, 0.5));
      } else if (e.key === 'r' || e.key === 'R') {
        setRotation((r) => (r + 90) % 360);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [src, closePreview]);

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.3, 4));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.3, 0.5));
  const handleRotate = () => setRotation((r) => (r + 90) % 360);

  const handleDownload = async () => {
    if (!src) return;
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `diasporawise-image-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(src, '_blank');
    }
  };

  const handleOpenNewTab = () => {
    if (src) window.open(src, '_blank');
  };

  return (
    <ImagePreviewContext.Provider
      value={{
        openPreview,
        closePreview,
        isPreviewOpen: Boolean(src),
      }}
    >
      {children}

      {/* Global Fullscreen Lightbox Modal */}
      <AnimatePresence>
        {src && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[99999] flex flex-col items-center justify-between bg-black/90 backdrop-blur-xl select-none"
            onClick={closePreview}
          >
            {/* Header Toolbar */}
            <div
              className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 text-white max-w-md truncate">
                <div className="p-2 bg-white/10 rounded-xl backdrop-blur-md">
                  <ImageIcon className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-sm font-bold truncate text-gray-100">
                    {title || 'Image Preview'}
                  </h3>
                  <p className="text-[11px] text-gray-400">Click anywhere outside to close</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleZoomIn}
                  className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-md"
                  title="Zoom In (+)"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  onClick={handleZoomOut}
                  className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-md"
                  title="Zoom Out (-)"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button
                  onClick={handleRotate}
                  className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-md"
                  title="Rotate (R)"
                >
                  <RotateCw className="w-4 h-4" />
                </button>

                <div className="h-5 w-[1px] bg-white/20 mx-1" />

                <button
                  onClick={handleDownload}
                  className="p-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold transition-all shadow-lg shadow-blue-500/30 flex items-center gap-1.5 text-xs"
                  title="Download Image"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Download</span>
                </button>

                <button
                  onClick={handleOpenNewTab}
                  className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all backdrop-blur-md"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>

                <button
                  onClick={closePreview}
                  className="p-2.5 rounded-xl bg-red-600/80 hover:bg-red-600 text-white transition-all backdrop-blur-md ml-2"
                  title="Close (ESC)"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Main Image Container */}
            <div
              className="flex-1 w-full flex items-center justify-center p-4 overflow-hidden relative cursor-grab active:cursor-grabbing"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.img
                src={src}
                alt={title}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: zoom, rotate: rotation, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="max-h-[85vh] max-w-[90vw] object-contain rounded-2xl shadow-2xl transition-transform duration-200"
                style={{
                  transformOrigin: 'center center',
                }}
                onDoubleClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
              />
            </div>

            {/* Footer Control Hint */}
            <div
              className="w-full py-3 px-6 text-center text-xs text-gray-400 bg-gradient-to-t from-black/80 to-transparent z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <span>Double click image to toggle 2x zoom • Press <kbd className="px-1.5 py-0.5 bg-white/20 rounded text-[10px] text-white">ESC</kbd> to exit</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </ImagePreviewContext.Provider>
  );
};
